import React from 'react'

import { Badge, Center, Stack, Text, Tooltip } from '@mantine/core'
import CsvIcon from '@mui/icons-material/ListAlt'
import { Grid, Typography } from '@mui/material'
import {
  BooleanField,
  BulkActionProps,
  Button,
  Datagrid,
  FunctionField,
  List,
  RaRecord,
  useNotify,
  useRefresh,
  useTranslate,
  useUnselectAll,
} from 'react-admin'
import { LXField } from '../../../components/LXField'
import { API } from '../../../helpers/Api.helper'
import { useUserStore } from '../../../stores/useUserStore'
import { StatementId } from '../../../types/Statement'
import { RegenerateStatementsModal } from '../../Statements/RegenerateStatementsModal'
import { LXNumberField } from './../../../components/LXNumberField'
import { LXStatementsActions } from './../../../components/LXStatementsActions'
import { NumberFormatter } from './../../../helpers/NumberFormatter.helper'
import { BeneficiarySubType } from './../../../types/Beneficiary'
import { CreateStatementModal } from './../../Statements/CreateStatementModal'
import { StatementDownloadsModal } from './../../Statements/StatementDownloadsModal'
import styles from './../Beneficiary.module.scss'

export const BeneficiaryStatementsShow = ({
  beneficiaryId,
  beneficiaryName,
}: BeneficiarySubType): JSX.Element => {
  const translate = useTranslate()
  const refresh = useRefresh()
  const notify = useNotify()
  const userStore = useUserStore()

  const [isCreateStatementModalOpen, setIsCreateStatementModalOpen] = React.useState(false)
  const [isStatementDownloadsModalOpen, setIsStatementDownloadsModalOpen] = React.useState(false)
  const onCloseCreateStatementModalOpen = React.useCallback(
    (created: boolean) => {
      if (created) {
        refresh()
        notify(translate('pages.statements.created'), { type: 'success' })
      }

      setIsCreateStatementModalOpen(false)
    },
    [notify, translate, refresh],
  )

  const BeneficiaryStatementsBulkActionButtons = (props: Pick<BulkActionProps, 'selectedIds'>) => {
    const translate = useTranslate()
    const refresh = useRefresh()
    const notify = useNotify()
    const userStore = useUserStore()
    const unselectAll = useUnselectAll('statements')

    const { selectedIds } = props
    const [isRegenerateStatementModalOpen, setIsRegenerateStatementModalOpen] =
      React.useState(false)
    const onCloseRegenerateStatementModal = React.useCallback(
      (hasRegenerated: boolean) => {
        if (hasRegenerated) {
          unselectAll()
          refresh()
          notify(translate('pages.statements.regenerated'), { type: 'success' })
        }

        setIsRegenerateStatementModalOpen(false)
      },
      [unselectAll, notify, translate, refresh],
    )

    React.useEffect(
      () => () => {
        unselectAll()
      },
      [unselectAll],
    )

    return (
      <>
        <Tooltip
          label={`⚠ ${translate('pages.users.lackingPermission')} → ${translate(
            'pages.users.permissionsList.statements.edit',
          )}`}
          hidden={userStore.isAuthorised('statements.edit')}
        >
          <span>
            <Button
              label={translate('pages.beneficiaries.regenerateStatements')}
              onClick={() => setIsRegenerateStatementModalOpen(true)}
              disabled={!userStore.isAuthorised('statements.edit')}
            />
          </span>
        </Tooltip>
        <RegenerateStatementsModal
          statementIds={selectedIds as StatementId[]}
          isOpen={isRegenerateStatementModalOpen}
          onClose={onCloseRegenerateStatementModal}
        />
      </>
    )
  }

  return (
    <>
      <Grid container direction="row" className={styles.edit} lg={12}>
        {userStore.isAuthorised('statements.edit') && (
          <>
            <LXField
              elementType={BooleanField}
              labeled
              source="statementSettings.introduction"
              label={translate('pages.beneficiaries.statementSettings.introduction')}
              size={2}
            />
            <LXField
              elementType={BooleanField}
              labeled
              source="statementSettings.globalInformations"
              label={translate('pages.beneficiaries.statementSettings.globalInformation')}
              size={2}
            />
            <LXField
              elementType={BooleanField}
              labeled
              source="statementSettings.firstPageLXGreetings"
              label={translate('pages.beneficiaries.statementSettings.firstPageLXGreetings')}
              size={2}
            />
            <LXField
              elementType={BooleanField}
              labeled
              source="statementSettings.products"
              label={translate('pages.beneficiaries.statementSettings.products')}
            />
            <LXField
              elementType={BooleanField}
              labeled
              source="statementSettings.productTypes"
              label={translate('pages.beneficiaries.statementSettings.productTypes')}
            />
            <LXField
              elementType={BooleanField}
              labeled
              source="statementSettings.artworks"
              label={translate('pages.beneficiaries.statementSettings.artworks')}
            />
            <LXField
              elementType={BooleanField}
              labeled
              source="statementSettings.shorts"
              label={translate('pages.beneficiaries.statementSettings.shorts')}
            />
            <LXField
              elementType={BooleanField}
              labeled
              source="statementSettings.adSubscriptionBreakdown"
              label={translate('pages.beneficiaries.statementSettings.adSubscriptionBreakdown')}
            />
            <LXField
              elementType={BooleanField}
              labeled
              source="statementSettings.monthlyComparison"
              label={translate('pages.beneficiaries.statementSettings.monthlyComparison')}
            />
            <LXField
              elementType={BooleanField}
              labeled
              source="statementSettings.yearlyComparison"
              label={translate('pages.beneficiaries.statementSettings.yearlyComparison')}
            />
          </>
        )}
        <Grid item className={styles.newStatement}>
          <Grid container direction="column" alignItems="flex-end">
            <Tooltip
              label={`⚠ ${translate('pages.users.lackingPermission')} → ${translate(
                'pages.users.permissionsList.statements.edit',
              )}`}
              hidden={userStore.isAuthorised('statements.edit')}
            >
              <span>
                <Button
                  label={translate('pages.beneficiaries.createStatement')}
                  onClick={() => setIsCreateStatementModalOpen(true)}
                  disabled={!userStore.isAuthorised('statements.edit')}
                />
              </span>
            </Tooltip>
            <Button
              label={translate('pages.statements.periodicStatementDownloads')}
              onClick={() => setIsStatementDownloadsModalOpen(true)}
            >
              <CsvIcon />
            </Button>
          </Grid>
        </Grid>
      </Grid>
      {beneficiaryId && (
        <List
          resource="statements"
          filter={{ beneficiaryId }}
          perPage={API.DEFAULT_PAGINATION.perPage}
          exporter={false}
          sort={{ field: 'period', order: 'DESC' }}
          disableSyncWithLocation
        >
          <Datagrid bulkActionButtons={<BeneficiaryStatementsBulkActionButtons />}>
            <FunctionField
              source="period"
              label={`${translate('_generics.month._title')} / ${translate('_generics.year')}`}
              render={(record: RaRecord | undefined) => (
                <span>{`${record?.month.toString().padStart(2, '0')} / ${record?.year}`}</span>
              )}
            />
            <LXNumberField source="videosViews" format="large" />
            <LXNumberField source="youtubeGeneratedRevenue" format="euros" />
            <LXNumberField source="ugcGeneratedRevenue" format="euros" />
            <LXNumberField source="generatedRevenue" format="euros" />
            <FunctionField
              source="netRevenue"
              label={translate('pages.statements.netRevenue')}
              sortable={false}
              render={(record: RaRecord | undefined) => (
                <Stack gap={4} py={4}>
                  {NumberFormatter.formatEuros(record?.netRevenue)}
                  <Text size="xs" c="grey">
                    {translate('pages.beneficiaries.rate')} :{' '}
                    <Text>{NumberFormatter.formatRate(record?.appliedRate)}</Text>
                  </Text>
                </Stack>
              )}
            />
            <FunctionField
              source="adjustmentRows"
              label={translate('pages.statements.adjustment')}
              sortable={false}
              render={(record: RaRecord | undefined) => (
                <div className={styles.numberChipContainer}>
                  {record?.adjustmentRows?.map((row: any) => (
                    <Typography
                      key={row.id}
                      component={'div'}
                      variant="body2"
                      className={styles.numberChip}
                    >
                      {NumberFormatter.formatEuros(row.amountInEuros)}
                    </Typography>
                  ))}
                </div>
              )}
            />
            <FunctionField
              source="paidAt"
              label={translate('_generics.status')}
              sortable={false}
              render={(record: RaRecord | undefined) => (
                <Center>
                  {record?.paidAt ? (
                    <Badge color="green" size="xs" pt="0.1rem">
                      {translate('pages.statements.paid')}
                    </Badge>
                  ) : (
                    <Badge color="yellow" size="xs" w="4rem" pt="0.1rem">
                      {translate('pages.statements.notPaid')}
                    </Badge>
                  )}
                </Center>
              )}
            />
            <LXStatementsActions />
          </Datagrid>
        </List>
      )}
      <CreateStatementModal
        beneficiaryId={beneficiaryId}
        isOpen={isCreateStatementModalOpen}
        onClose={onCloseCreateStatementModalOpen}
      />
      <StatementDownloadsModal
        beneficiaryId={beneficiaryId}
        isOpen={isStatementDownloadsModalOpen}
        onClose={() => setIsStatementDownloadsModalOpen(false)}
        beneficiaryName={beneficiaryName}
      />
    </>
  )
}
